// src/pages/admin/AdminUserDashboard.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { useNotification } from '../../contexts/NotificationContext';
import userManagementService from '../../services/userManagementService';

import { HeadingMedium } from 'baseui/typography';
import { TableBuilder, TableBuilderColumn } from 'baseui/table-semantic';
import { Spinner } from 'baseui/spinner';
import { Block } from 'baseui/block';
import { Button } from 'baseui/button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalButton,
} from 'baseui/modal';
import { RadioGroup, Radio } from 'baseui/radio';
import { Pagination } from 'baseui/pagination';

const AdminUserDashboard = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedTier, setSelectedTier] = useState('free');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);

  const { currentUser } = useAuth();
  const { t } = useLocale();
  const { showNotification } = useNotification();

  const fetchUsers = useCallback(async (currentPage, currentRowsPerPage) => {
    setIsLoading(true);
    const offset = (currentPage - 1) * currentRowsPerPage;
    const { data, error } = await userManagementService.getAllUserProfiles(currentRowsPerPage, offset);
    if (error) {
      showNotification(error.message || t('errors.userManagement.fetchFailed'), 'error');
    } else if (data?.profiles) {
      setUsers(data.profiles);
      setTotalUsers(data.totalCount);
    }
    setIsLoading(false);
  }, [showNotification, t]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers(page, rowsPerPage);
    } else {
      showNotification(t('errors.admin.notAuthorized'), 'error');
      setIsLoading(false);
    }
  }, [currentUser, fetchUsers, page, rowsPerPage, showNotification, t]);

  const handleOpenModal = (user) => {
    setEditingUser(user);
    setSelectedTier(user.tier);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleConfirmTierChange = async () => {
    if (!editingUser) return;

    const { data, error: serviceError } = await userManagementService.updateUserTier(editingUser.id, selectedTier);

    if (data?.success) {
      showNotification(t('admin.users.notification.success'), 'success');
      fetchUsers(page, rowsPerPage);
    } else {
      showNotification(serviceError?.message || t('admin.users.notification.error'), 'error');
    }
    handleCloseModal();
  };

  const handleDeleteUser = async (user) => {
    if (window.confirm(t('admin.users.confirmDelete', { email: user.email }))) {
      const { data, error: serviceError } = await userManagementService.deleteUser(user.id);

      if (data?.success) {
        showNotification(t('admin.users.notification.deleteSuccess'), 'success');
        fetchUsers(page, rowsPerPage);
      } else {
        showNotification(serviceError?.message || t('admin.users.notification.deleteError'), 'error');
      }
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Block>
      <HeadingMedium>{t('admin.users.title')}</HeadingMedium>
      <TableBuilder data={users}>
        <TableBuilderColumn header={t('admin.users.header.userId')}>
          {(row) => row.id}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.users.header.email')}>
          {(row) => row.email}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.users.header.role')}>
          {(row) => row.role}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.users.header.tier')}>
          {(row) => row.tier}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.users.header.limit')}>
          {(row) => row.tournament_limit}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.users.header.createdAt')}>
          {(row) => new Date(row.created_at).toLocaleString()}
        </TableBuilderColumn>
        <TableBuilderColumn header={t('admin.users.header.actions')}>
          {(row) => (
            <>
              <Button size="compact" onClick={() => handleOpenModal(row)}>
                {t('admin.users.actions.changeTier')}
              </Button>
              <Button
                size="compact"
                kind="minimal"
                onClick={() => handleDeleteUser(row)}
                overrides={{
                  BaseButton: {
                    style: ({ $theme }) => ({
                      color: $theme.colors.negative,
                      marginLeft: '8px',
                    }),
                  },
                }}
              >
                {t('admin.users.actions.deleteUser')}
              </Button>
            </>
          )}
        </TableBuilderColumn>
      </TableBuilder>

      <Modal onClose={handleCloseModal} isOpen={isModalOpen}>
        <ModalHeader>{t('admin.users.modal.title')} - {editingUser?.email}</ModalHeader>
        <ModalBody>
          <RadioGroup
            value={selectedTier}
            onChange={(e) => setSelectedTier(e.currentTarget.value)}
            name="tier-selection"
          >
            <Radio value="free">{t('tier.free')}</Radio>
            <Radio value="paid">{t('tier.paid')}</Radio>
          </RadioGroup>
        </ModalBody>
        <ModalFooter>
          <ModalButton kind="tertiary" onClick={handleCloseModal}>
            {t('common.modalCancel')}
          </ModalButton>
          <ModalButton onClick={handleConfirmTierChange}>
            {t('common.modalSave')}
          </ModalButton>
        </ModalFooter>
      </Modal>

      <Pagination
        currentPage={page}
        numPages={Math.ceil(totalUsers / rowsPerPage)}
        onPageChange={({ nextPage }) => setPage(nextPage)}
        onPrevClick={() => setPage(page - 1)}
        onNextClick={() => setPage(page + 1)}
        onPageSizeChange={({ size }) => setRowsPerPage(size)}
        pageSize={rowsPerPage}
        labels={{
          prevButton: t('common.prev'),
          nextButton: t('common.next'),
        }}
      />
    </Block>
  );
};

export default AdminUserDashboard;